import React, { useEffect, useState } from 'react';
import { ScrollView } from 'react-native';
import { useForm } from 'react-hook-form';
import { MainLayout, MainLayoutHeader, Form } from '@app/components';
import { SearchInput } from '@app/components/form/SearchInput';
import { DateTimePicker } from '@app/components/form/DateTimePicker';
import { serviceApi } from '@app/store';
import { OrderSearchResultCard } from '@app/features/Service';
import styled from '@2pulse/responsive';
import { normalize } from '@app/helpers';
import { theme } from '@app/theme';

const PERIODS = [
  { label: '7 jours', days: 7 },
  { label: '30 jours', days: 30 },
  { label: '90 jours', days: 90 },
] as const;

export const OrderSearchScreen = () => {
  const [periodDays, setPeriodDays] = useState<number | 'custom'>(30);
  const [trigger, { data, isFetching }] = serviceApi.useLazySearchOrdersQuery();

  // SearchInput/DateTimePicker exigent un contexte <Form> (react-hook-form) : ils appellent
  // useInput -> useFormContext en interne et plantent au rendu sans FormProvider (cf. SimpleList.tsx,
  // CustomerExportScreen.tsx). On pilote donc q/customStart/customEnd via un formulaire local.
  const formMethods = useForm({
    defaultValues: { q: '', customStart: '', customEnd: '' },
  });

  const q = formMethods.watch('q');
  const customStart = formMethods.watch('customStart');
  const customEnd = formMethods.watch('customEnd');

  const isCustomPeriodIncomplete = periodDays === 'custom' && (!customStart || !customEnd);

  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed.length < 2) return;
    if (isCustomPeriodIncomplete) return;

    const handle = setTimeout(() => {
      let startDate: Date;
      let endDate: Date;

      if (periodDays === 'custom') {
        // customStart/customEnd sont des ISO strings complètes (field.onChange(date.toISOString())
        // côté DateTimePicker.web.tsx) : on part de l'objet Date et on cale les bornes de journée
        // en heure locale via setHours, sans concaténer de chaîne.
        startDate = new Date(customStart);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(customEnd);
        endDate.setHours(23, 59, 59, 999);
      } else {
        endDate = new Date();
        startDate = new Date(endDate.getTime() - periodDays * 24 * 60 * 60 * 1000);
      }

      trigger({ q: trimmed, startDate: startDate.toISOString(), endDate: endDate.toISOString() });
    }, 400);

    return () => clearTimeout(handle);
  }, [q, periodDays, customStart, customEnd, isCustomPeriodIncomplete]);

  return (
    <MainLayout header={<MainLayoutHeader tabs={[{ label: 'Recherche', screenName: 'OrderSearchScreen', permissions: ['dashboard::services.read'] }]} />}>
      <SearchContainer>
        <Form formMethods={formMethods}>
          <SearchInput
            name="q"
            label=""
            placeholder="N° de commande, n° plateforme, nom, téléphone, adresse..." />

          <PeriodRow>
            {PERIODS.map(({ label, days }) => (
              <PeriodButton key={days} active={periodDays === days} onPress={() => setPeriodDays(days)}>
                <PeriodLabel active={periodDays === days}>{label}</PeriodLabel>
              </PeriodButton>
            ))}
            <PeriodButton active={periodDays === 'custom'} onPress={() => setPeriodDays('custom')}>
              <PeriodLabel active={periodDays === 'custom'}>Personnalisé</PeriodLabel>
            </PeriodButton>
          </PeriodRow>

          {periodDays === 'custom' && (
            <PeriodRow>
              <DateTimePicker name="customStart" type="date" label="Du" onWhite />
              <DateTimePicker name="customEnd" type="date" label="Au" onWhite />
            </PeriodRow>
          )}
        </Form>

        <ScrollView>
          {q.trim().length < 2 && <Hint>Tapez au moins 2 caractères pour lancer la recherche.</Hint>}
          {q.trim().length >= 2 && isCustomPeriodIncomplete && <Hint>Sélectionnez les deux dates de la période.</Hint>}
          {q.trim().length >= 2 && !isCustomPeriodIncomplete && isFetching && <Hint>Recherche en cours...</Hint>}
          {!isFetching && q.trim().length >= 2 && !isCustomPeriodIncomplete && data?.results?.length === 0 && <Hint>Aucune commande trouvée sur la période.</Hint>}
          {!isFetching && q.trim().length >= 2 && !isCustomPeriodIncomplete && data?.hasMore && <Warning>Plus de 50 résultats — affinez votre recherche ou réduisez la période.</Warning>}
          {!isFetching && q.trim().length >= 2 && !isCustomPeriodIncomplete && (data?.results ?? []).map((result) => (
            <OrderSearchResultCard key={result._id} result={result} />
          ))}
        </ScrollView>
      </SearchContainer>
    </MainLayout>
  );
};

// La sidebar est en overlay : chaque écran compense sa largeur lui-même
// (même pattern que CatalogCalendarHeader).
const SearchContainer = styled.View({
  width: '100%',
  height: '100%',
  padding: normalize(21),
  paddingLeft: theme.sizes.sidebarWidth + normalize(21),
  rowGap: normalize(13),
});
const PeriodRow = styled.View({ display: 'flex', flexDirection: 'row', columnGap: normalize(10), alignItems: 'center' });
const PeriodButton = styled.TouchableOpacity<{ active?: boolean }>(({ props }) => ({
  borderRadius: normalize(10),
  paddingHorizontal: normalize(13),
  paddingVertical: normalize(8),
  // Fond de page clair : le pill inactif reprend le panneau sombre maison (cf. SimpleList).
  backgroundColor: props.active ? theme.colors.celadonblue : '#0B2032',
}));
const PeriodLabel = styled.Text<{ active?: boolean }>(({ props }) => ({
  color: theme.colors.babypowder,
  fontWeight: props.active ? 'bold' : 'normal',
}));
const Hint = styled.Text({ color: theme.colors.satanGray, fontStyle: 'italic', padding: normalize(13) });
const Warning = styled.Text({ color: theme.colors.gold, fontWeight: 'bold', padding: normalize(13) });
